import { forwardRef, useId, type InputHTMLAttributes } from 'react';
import { cn } from '@/utils/cn';
import { Icon } from '../Icon';
import type { IconName } from '../Icon';
import { FormField } from '../FormField/FormField';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Field label — rendered as a `<label>` above the input via the Primer Typography component. */
  label?: string;
  /**
   * Error message. When provided:
   * - Activates error state styling (border, background)
   * - Replaces `helperText` in the text slot below the input
   * - Text is rendered in `text-error` colour
   */
  error?: string;
  /**
   * Helper text shown below the input when there is no `error`.
   * Rendered in `text-bodylight` colour via the Primer Typography component.
   */
  helperText?: string;
  /**
   * Optional icon rendered on the left side of the input field.
   * Uses `currentColor` — colour inherits from the input wrapper's text colour.
   */
  icon?: IconName;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

/**
 * Styles applied to the visual input wrapper (`<div>`) that wraps the icon and
 * the native `<input>` element.
 *
 * The wrapper — not the `<input>` itself — carries border, background, radius,
 * and padding so the icon sits inside the visual field boundary.
 */
const wrapperBase =
  'flex items-center gap-4 px-4 py-4 rounded-small border bg-surface transition-colors duration-150';

const wrapperDefault = 'border-default focus-within:border-(--border-color-interactiveloud)';

const wrapperError = 'border-error bg-error';

const wrapperReadOnly = 'border-default bg-disabled';

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Primer Input component.
 *
 * The standard form text field. Handles label, optional icon, helper text, and
 * error state as first-class concerns, all wired up with the correct Primer
 * design tokens.
 *
 * ### States
 * - **Default** — `border-default`, `bg-surface`, `text-body`
 * - **Error** — `border-error`, `bg-error`; helper text slot shows `error` in `text-error`
 * - **Read-only** — `border-default`, `bg-disabled`, trailing lock icon
 *
 * ### Layout stability
 * The sub-text row (helper / error text) is always present in the DOM so the
 * field never changes height when transitioning into or out of error state.
 *
 * ### Accessibility
 * The label is always associated with the input via a matching `htmlFor`/`id`
 * pair. When no `id` is provided by the consumer, a stable id is generated
 * automatically via React's `useId` hook so the association is always correct.
 *
 * ### Typography
 * - Label — `Typography variant="formLabel" size="default"` (renders a `<label>`)
 * - Helper / error text — `Typography variant="body" size="small"`
 * - Input value / placeholder — `body-small` class applied directly to the
 *   native `<input>` element (Typography cannot wrap a void element)
 *
 * @example
 * // Basic
 * <Input label="Email" type="email" placeholder="you@example.com" />
 *
 * @example
 * // With icon
 * <Input label="Search" icon="search" placeholder="Search campaigns…" />
 *
 * @example
 * // Error state
 * <Input label="Email" error="Please enter a valid email address" />
 *
 * @example
 * // With helper text
 * <Input label="Username" helperText="Visible on your public profile" />
 *
 * @see src/primer/README.md
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, helperText, icon, id, readOnly = false, className = '', ...props },
  ref
) {
  // Stable generated id — used when the consumer does not supply one so the
  // label <htmlFor> always has a matching input id.
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const hasError = Boolean(error);
  const isReadOnly = readOnly && !hasError;

  return (
    <FormField label={label} error={error} helperText={helperText} inputId={inputId}>
      {/* ── Input wrapper ────────────────────────────────────────────────
       * The visual border/background lives on this wrapper div so the icon
       * appears inside the field boundary. The native <input> is transparent
       * and fills the remaining space.
       */}
      <div
        data-input-wrapper=""
        className={cn(
          wrapperBase,
          hasError ? wrapperError : isReadOnly ? wrapperReadOnly : wrapperDefault,
          className
        )}
      >
        {/* ── Icon (optional, left side) ─────────────────────────────── */}
        {icon && (
          <span aria-hidden="true" className="inline-flex shrink-0 text-bodylight">
            <Icon name={icon} size="large" />
          </span>
        )}

        {/* ── Native input ────────────────────────────────────────────── */}
        <input
          ref={ref}
          id={inputId}
          className={cn(
            // Typography — body-small
            'body-small',
            // Layout — fill remaining space after the icon
            'min-w-0 flex-1',
            // Transparent background so the wrapper bg token shows through
            'bg-transparent',
            // Text colours — error state switches value text to text-error
            hasError
              ? 'text-error placeholder:text-error'
              : isReadOnly
                ? 'text-body cursor-default'
                : 'text-body placeholder:text-bodylight',
            // Remove native focus ring — the wrapper handles focus visually
            'outline-none',
            // Disabled
            'disabled:cursor-not-allowed disabled:text-disabled'
          )}
          readOnly={readOnly}
          {...props}
        />
        {isReadOnly && <Icon name="lock" size="large" color="light" aria-hidden="true" />}
      </div>
    </FormField>
  );
});

export default Input;
